import { useState, useEffect, useCallback } from 'react';
import { getAugmentationJobByExtractionJobId, retryAugmentationJob } from '../services/api/augmentationApi';

/**
 * Custom hook for managing augmentation job data
 * @param {string} extractionJobId - The extraction job ID
 * @param {boolean} shouldFetch - Whether to fetch the augmentation job
 * @returns {Object} Augmentation job state and actions
 */
export const useAugmentationJob = (extractionJobId, shouldFetch = false) => {
  const [augmentationJob, setAugmentationJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const fetchAugmentationJob = useCallback(async () => {
    if (!extractionJobId || !shouldFetch) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await getAugmentationJobByExtractionJobId(extractionJobId);
      
      // The API already returns parsed objects - use the response directly
      setAugmentationJob(response.data);
    } catch (err) {
      // 404 is expected when no augmentation job exists yet
      if (err.response?.status !== 404) {
        console.error('Error fetching augmentation job:', err);
        setError(err.response?.data?.message || 'Failed to fetch augmentation job');
      } else {
        setAugmentationJob(null);
      }
    } finally {
      setLoading(false);
    }
  }, [extractionJobId, shouldFetch]);

  const handleRetry = useCallback(async () => {
    if (!augmentationJob?.id) {
      setError('No augmentation job found to retry');
      return;
    }

    setRetrying(true);
    try {
      await retryAugmentationJob(augmentationJob.id);
      // Refresh the augmentation job data after retry
      await fetchAugmentationJob();
      return { success: true };
    } catch (err) {
      console.error('Error retrying augmentation job:', err);
      const errorMessage = err.response?.data?.message || 'Failed to retry augmentation job';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setRetrying(false);
    }
  }, [augmentationJob?.id, fetchAugmentationJob]);

  // Fetch augmentation job when conditions are met
  useEffect(() => {
    fetchAugmentationJob();
  }, [fetchAugmentationJob]);

  return {
    augmentationJob,
    loading,
    error,
    retrying,
    fetchAugmentationJob,
    handleRetry,
    setError
  };
};